import React, { useEffect, useState } from "react";
import { Play, Loader2, RefreshCw, AlertTriangle, CheckCircle, FileText, X } from "lucide-react";

// ----------- LIVE AWS CONFIGURATION -----------
const API_ENDPOINT = "https://vxmes7oafk.execute-api.eu-north-1.amazonaws.com/prod/payslip_hrms";

async function callPayrollApi(action, payload = {}) {
  try {
    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      // Removed credentials: "omit" to allow standard CORS flow
      headers: { 
        "Content-Type": "application/json" 
      },
      // Ensure the action is merged correctly into the top level of the body
      body: JSON.stringify({ action, ...payload })
    });

    const json = await response.json();
    
    // The backend returns success: false for 400 errors, so we catch that here
    if (!response.ok || json?.success === false) {
      throw new Error(json?.message || `Error ${response.status}: Action failed`);
    }
    return json.data;
  } catch (err) {
    console.error("API Error Details:", err);
    throw err;
  }
}

export default function PayrollRunRollback() {
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: 2025 });
  const [runs, setRuns] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [toast, setToast] = useState(null);

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  const getFormattedMonth = () => `${months.find(m => m.value === period.month)?.label} ${period.year}`;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await callPayrollApi("list_requests");
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const executePayroll = async () => {
    setShowConfirmModal(false);
    setIsRunning(true);
    try {
      // FIXED: Ensuring payload matches backend expectations exactly
      await callPayrollApi("generate_bulk", {
        employees: ["EMP001", "EMP002"], // Array of strings
        month: getFormattedMonth(),      // String
        ctc: 1200000                     // Numeric Number (not a string)
      });
      showToast("Payroll processed successfully!", "success");
      fetchHistory();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleDownload = async (emp_code, month) => {
    showToast(`Downloading payslip for ${emp_code}...`, "success");
    try {
      const data = await callPayrollApi("download_payslip", { emp_code, month });
      
      if (!data?.url) {
        throw new Error("No download URL returned from server.");
      }

      // Open in new tab (Safest method for S3 Pre-signed URLs with CORS)
      window.open(data.url, '_blank', 'noopener,noreferrer');

    } catch (err) {
      console.error("Download Error:", err);
      showToast(`Error: ${err.message}`, "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-8 font-poppins relative">
      
      {/* -------- BOTTOM-RIGHT TOAST -------- */}
      {toast && (
        <div className={`fixed bottom-10 right-10 z-[300] px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-in slide-in-from-right fade-in duration-300
          ${toast.type === "error" ? "bg-white border-l-4 border-red-500 text-red-600" : "bg-white border-l-4 border-emerald-500 text-emerald-600"}`}>
          {toast.type === "error" ? <AlertTriangle size={24} /> : <CheckCircle size={24} />}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 leading-none mb-1">System Notification</span>
            <span className="font-bold text-sm text-slate-700">{toast.message}</span>
          </div>
          <button onClick={() => setToast(null)} className="ml-4 text-slate-300 hover:text-slate-500">
            <X size={18} />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Payroll Engine</h1>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              Connected to wysele.in Production
            </p>
          </div>
          
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex bg-slate-100 rounded-xl p-1 border border-slate-200">
              <select 
                value={period.month} 
                onChange={e => setPeriod({ ...period, month: Number(e.target.value) })} 
                className="bg-transparent px-4 py-2 font-semibold text-slate-700 outline-none cursor-pointer"
              >
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="w-px h-6 bg-slate-200 my-auto"></div>
              <input 
                type="number" 
                value={period.year} 
                onChange={e => setPeriod({ ...period, year: Number(e.target.value) })} 
                className="bg-transparent w-24 px-4 font-semibold text-slate-700 outline-none" 
              />
            </div>
            
            <button 
              onClick={() => setShowConfirmModal(true)} 
              disabled={isRunning} 
              className="bg-slate-900 text-white px-8 py-3 rounded-xl flex items-center gap-2 hover:bg-slate-800 active:scale-95 transition-all shadow-lg shadow-slate-200 disabled:opacity-50"
            >
              {isRunning ? <Loader2 className="animate-spin" size={18} /> : <Play size={18} />}
              Run Payroll
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-8 py-6 flex justify-between items-center border-b border-slate-100 bg-slate-50/50">
            <div>
              <h2 className="font-bold text-slate-800">Execution History</h2>
              <p className="text-xs text-slate-500">Recent payroll generation records from database</p>
            </div>
            <button 
              onClick={fetchHistory} 
              className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all border border-transparent hover:border-slate-100"
            >
              <RefreshCw size={18} className={`${loadingHistory ? "animate-spin text-blue-500" : "text-slate-400"}`} />
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-400 font-bold border-b border-slate-100">
                <tr>
                  <th className="px-8 py-5">Employee Detail</th>
                  <th className="px-8 py-5">Payroll Period</th>
                  <th className="px-8 py-5">System Status</th>
                  <th className="px-8 py-5 text-right">Documents</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center opacity-40">
                        <RefreshCw size={40} className="mb-4 text-slate-300" />
                        <p className="text-slate-500 font-medium italic">No recent payroll records found</p>
                      </div>
                    </td>
                  </tr>
                ) : runs.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-8 py-5 font-bold text-slate-700">{r.emp_code}</td>
                    <td className="px-8 py-5 text-slate-600">{r.month}</td>
                    <td className="px-8 py-5">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter
                        ${r.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${r.status === "completed" ? "bg-emerald-500" : "bg-orange-500"}`}></span>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <button 
                        onClick={() => handleDownload(r.emp_code, r.month)} 
                        className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-5 py-2 rounded-xl text-xs font-bold hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all shadow-sm"
                      >
                        <FileText size={14} /> View Payslip
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* -------- CONFIRM MODAL -------- */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[400] p-4">
          <div className="bg-white p-10 rounded-[2.5rem] max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="text-orange-500" size={40} />
            </div>
            <h3 className="text-2xl font-bold text-slate-800">Process Payroll?</h3>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              This will generate official payslips for all selected employees for <span className="font-bold text-slate-700">{getFormattedMonth()}</span>.
            </p>
            <div className="mt-10 space-y-3">
              <button 
                onClick={executePayroll} 
                className="w-full bg-slate-900 text-white py-4 rounded-2xl font-bold shadow-lg shadow-slate-200 hover:bg-slate-800 active:scale-95 transition-all"
              >
                Confirm & Execute
              </button>
              <button 
                onClick={() => setShowConfirmModal(false)} 
                className="w-full bg-white text-slate-400 py-4 rounded-2xl font-bold hover:text-slate-600 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}